import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import { UserKeys, bulkSetOrganization } from '../../../../api/users-api';
import OrganizationsAutoComplete from '../../../../shared/organizations-autocomplete';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
interface SetOrgsProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  userRoles: string[];
  changeLogRoles: string[];
}

export default function SetOrgsDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  userRoles,
  changeLogRoles,
}: SetOrgsProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [orgTarget, setOrgTarget] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isSetting } = useMutation({
    mutationFn: () =>
      bulkSetOrganization('User', selectedUsers, orgTarget, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to set the user(s) to the organization has started.',
      });
      selectedUsers.forEach((user) => {
        queryClient.invalidateQueries({ queryKey: [UserKeys.User, user] });
      });
      setChangeLogMessage('');
      setOrgTarget('');
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding the selected user(s) to the organization. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_set_orgs, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.BulkSetOrganization.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setOrgTarget('');
      }}
      title="Set Organizational Unit"
      dialogContent={
        <Stack>
          {selectedUsers.length > 1 ? (
            <Typography>
              Set organizational unit for{' '}
              <strong>{selectedUsers.length}</strong> users.
            </Typography>
          ) : (
            <Typography>
              Set organizational unit for <strong>{selectedUsers[0]}</strong>.
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <OrganizationsAutoComplete
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.EditUserOrgUnit.value
                )
              }
              size="small"
              autoCompleteValue={orgTarget}
              setAutoCompleteValue={setOrgTarget}
              hasError={false}
              autoFocus
            />
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isSetting}
          />
        </Stack>
      }
      primaryButton={{
        loading: isSetting,
        action: handleSubmit,
        disabled: isSetting || orgTarget.length === 0,
        title: 'Set Unit',
      }}
    />
  );
}
